import { FlashList, ListRenderItem } from "@shopify/flash-list";
import { Box } from "native-base";
import React from "react";
import {
  LayoutChangeEvent,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { ListOfUnitsPropsV2 } from "../interfaces";
import { useAppContext } from "../../../Universal/API/Context-API/AppContext";
import { useCart } from "../../../Universal/API/Context-API/CartContext";
import { useSmartNavigate } from "../../../Universal/API/Context-API/hooks/useSmartNavigate";
import {
  UNIT_LIST_V3_CHUNK_SIZE,
  UNIT_LIST_V3_ITEM_TYPE,
} from "./constants";
import ListEmptyState from "./Components/ListEmptyState";
import UnitListItemV3 from "./Components/UnitListItemV3";
import {
  createListOfUnitsV3Styles,
} from "./styles";
import {
  getEstimatedItemSize,
  getFallbackNumColumns,
  getResolvedNumColumns,
} from "./helpers";
import { UnitIdentifier, UnitListEntry } from "./types";

const ListOfUnitsV3: React.FC<ListOfUnitsPropsV2> = ({
  listOfUnis,
  navigation,
  renderHeader,
  doReset,
  extraUnitParams,
  contentContainerStyle = {},
  listEmptyComponent,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const { isSmallVersion, isMediumVersion, scaleAll } = useAppContext();
  const { totalQuantity } = useCart();
  const styles = React.useMemo(() => createListOfUnitsV3Styles(scaleAll), [scaleAll]);
  const { smartNavigate } = useSmartNavigate({ navigation });
  const [containerWidth, setContainerWidth] = React.useState(0);
  const units = listOfUnis ?? [];

  const fallbackNumColumns = React.useMemo(
    () => getFallbackNumColumns({ isSmallVersion, isMediumVersion }),
    [isMediumVersion, isSmallVersion]
  );

  const layoutWidth = React.useMemo(() => {
    if (Platform.OS === "web") {
      return Math.max(containerWidth, windowWidth);
    }
    return windowWidth;
  }, [containerWidth, windowWidth]);

  const numColumns = React.useMemo(
    () => getResolvedNumColumns({ layoutWidth, fallbackNumColumns }),
    [fallbackNumColumns, layoutWidth]
  );

  const estimatedItemSize = React.useMemo(
    () =>
      getEstimatedItemSize({
        isSmallVersion,
        isMediumVersion,
        scaleAll,
      }),
    [isMediumVersion, isSmallVersion, scaleAll]
  );

  const initialNumToRender = React.useMemo(() => numColumns * 3, [numColumns]);
  const initialVisibleCount = React.useMemo(
    () => Math.max(initialNumToRender, UNIT_LIST_V3_CHUNK_SIZE),
    [initialNumToRender]
  );
  const gridHorizontalPadding = React.useMemo(() => {
    if (isSmallVersion) return 0;

    const desiredWidth = numColumns * 468 * scaleAll;
    if (layoutWidth <= desiredWidth) return 0;

    return Math.max(0, (layoutWidth - desiredWidth) / 2);
  }, [isSmallVersion, layoutWidth, numColumns, scaleAll]);

  const [visibleCount, setVisibleCount] = React.useState(initialVisibleCount);

  React.useEffect(() => {
    setVisibleCount(initialVisibleCount);
  }, [initialVisibleCount, units]);

  const visibleUnits = React.useMemo(
    () => units.slice(0, visibleCount),
    [units, visibleCount]
  );

  const mergedContentContainerStyle = React.useMemo(() => {
    const flattened = StyleSheet.flatten(contentContainerStyle) ?? {};
    const existingPaddingHorizontal =
      typeof flattened.paddingHorizontal === "number"
        ? flattened.paddingHorizontal
        : 0;

    return {
      ...styles.listContent,
      ...flattened,
      paddingHorizontal: existingPaddingHorizontal + gridHorizontalPadding,
    };
  }, [contentContainerStyle, gridHorizontalPadding, styles.listContent]);

  const listHeaderComponent = React.useMemo(
    () => {
      const header =
        (renderHeader as
          | React.ComponentType<any>
          | React.ReactElement
          | null
          | undefined) ?? undefined;

      if (!header) return undefined;

      return (
        <View style={{ marginHorizontal: -gridHorizontalPadding }}>
          {header as any}
        </View>
      );
    },
    [gridHorizontalPadding, renderHeader]
  );

  const listFooterComponent = React.useMemo(() => {
    if (totalQuantity <= 0) return undefined;

    return (
      <Box
        style={{
          width: "100%",
          height: (isSmallVersion ? 108 : 124) * scaleAll,
        }}
      />
    );
  }, [isSmallVersion, scaleAll, totalQuantity]);

  const handlePressUnit = React.useCallback(
    (idUnit: UnitIdentifier) => {
      smartNavigate({
        navigation,
        replace: doReset,
        goTo: "Unit",
        data: { idUnit, ...(extraUnitParams ?? {}) },
      });
    },
    [navigation, smartNavigate, doReset, extraUnitParams]
  );

  const handlePressCollection = React.useCallback(
    (collectionName: string) => {
      smartNavigate({
        navigation,
        goTo: "Collection",
        data: { collection_name: collectionName },
      });
    },
    [navigation, smartNavigate]
  );

  const renderItem = React.useCallback<ListRenderItem<UnitListEntry>>(
    ({ item, index }) => (
      <UnitListItemV3
        item={item}
        onPressUnit={handlePressUnit}
        onPressCollection={handlePressCollection}
        shouldLoadMedia={index < initialNumToRender}
        styles={styles}
      />
    ),
    [handlePressCollection, handlePressUnit, initialNumToRender, styles]
  );

  const keyExtractor = React.useCallback(
    (item: UnitListEntry, index: number) => item?.unit?.id ?? String(index),
    []
  );

  const handleEndReached = React.useCallback(() => {
    if (!units.length) return;

    setVisibleCount((prev) => {
      if (prev >= units.length) return prev;
      return Math.min(prev + UNIT_LIST_V3_CHUNK_SIZE, units.length);
    });
  }, [units]);

  const handleLayout = React.useCallback((event: LayoutChangeEvent) => {
    const nextWidth = Math.round(event.nativeEvent.layout.width);
    if (nextWidth > 0) {
      setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    }
  }, []);

  const flashListProps = React.useMemo(
    () => ({
      key: `${numColumns}:${Math.round(layoutWidth)}`,
      data: visibleUnits,
      estimatedItemSize,
      numColumns,
      keyExtractor,
      ListHeaderComponent: listHeaderComponent,
      ListFooterComponent: listFooterComponent,
      contentContainerStyle: mergedContentContainerStyle,
      ListEmptyComponent: listEmptyComponent ?? <ListEmptyState />,
      renderItem,
      drawDistance: estimatedItemSize * (Platform.OS === "web" ? 2.5 : 1.5),
      initialNumToRender,
      maxToRenderPerBatch: initialNumToRender,
      windowSize: Platform.OS === "web" ? 8 : 5,
      removeClippedSubviews: Platform.OS !== "web",
      getItemType: () => UNIT_LIST_V3_ITEM_TYPE,
      onEndReached: handleEndReached,
      onEndReachedThreshold: 0.5,
    }),
    [
      numColumns,
      layoutWidth,
      visibleUnits,
      estimatedItemSize,
      keyExtractor,
      listHeaderComponent,
      listFooterComponent,
      mergedContentContainerStyle,
      listEmptyComponent,
      renderItem,
      initialNumToRender,
      handleEndReached,
    ]
  );

  return (
    <Box style={styles.listContainer} onLayout={handleLayout}>
      <FlashList {...flashListProps} />
    </Box>
  );
};

export const defaultContentContainerStyleV3 = {};

export default React.memo(ListOfUnitsV3);
